import { BASE_API_URL } from '../apiConfig';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ConfirmationModal from '../components/ConfirmationModal';
import LandingPage from './LandingPage';
import { useTranslation } from 'react-i18next';

const Home = () => {
    const { t } = useTranslation();
    const token = localStorage.getItem('token');
    const [leagues, setLeagues] = useState([]);
    const [newLeagueName, setNewLeagueName] = useState('');
    const [sport, setSport] = useState('football');
    const [isPublic, setIsPublic] = useState(false);
    const [joinInviteCode, setJoinInviteCode] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [scoutingMatches, setScoutingMatches] = useState([]);
    const [modalConfig, setModalConfig] = useState({ isOpen: false, title: '', message: '' });
    const [tutorialOpen, setTutorialOpen] = useState(false);

    const sportInfo = {
        football: { icon: '⚽', label: t('football') },
        volleyball: { icon: '🏐', label: t('volleyball') },
        padel: { icon: '🎾', label: t('padel') },
        basketball: { icon: '🏀', label: t('basketball') },
        tennis: { icon: '🎾', label: t('tennis') },
        waterpolo: { icon: '🤽', label: t('waterpolo') },
        rugby: { icon: '🏉', label: t('rugby') }
    };

    const fetchLeagues = async () => {
        if (!token) return;
        const res = await fetch(`${BASE_API_URL}/leagues`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        const leaguesList = Array.isArray(data) ? data : [];
        setLeagues(leaguesList);
        if (leaguesList.length === 0) {
            setTutorialOpen(true);
        }
    };

    const fetchScoutingMatches = async () => {
        if (!token) return;
        const res = await fetch(`${BASE_API_URL}/scouting`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setScoutingMatches(Array.isArray(data) ? data : []);
    };

    const handleCreateLeague = async (e) => {
        e.preventDefault();
        await fetch(`${BASE_API_URL}/leagues`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ name: newLeagueName, is_public: isPublic, sport: sport })
        });
        setNewLeagueName('');
        setSport('football');
        setIsPublic(false);
        setShowCreateForm(false);
        fetchLeagues();
    };

    const handleJoinLeague = async (e) => {
        e.preventDefault();
        const res = await fetch(`${BASE_API_URL}/leagues/join`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ inviteCode: joinInviteCode })
        });
        const data = await res.json();
        if (res.ok) {
            setModalConfig({ isOpen: true, title: t('success'), message: data.message || t('pending_request') });
            setJoinInviteCode('');
            fetchLeagues();
        } else {
            setModalConfig({ isOpen: true, title: t('error'), message: data.error || t('error') });
        }
    };

    const handleJoinScouting = async (requestId) => {
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/scouting/${requestId}/join`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setModalConfig({
                    isOpen: true,
                    title: "✅ Candidatura Inviata",
                    message: "La tua candidatura è stata inviata all'amministratore. Riceverai una notifica se verrai accettato!"
                });
                fetchScoutingMatches();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleWithdrawScouting = async (requestId) => {
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/scouting/${requestId}/withdraw`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                fetchScoutingMatches();
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (token) {
            fetchLeagues();
            fetchScoutingMatches();
        }
    }, [token]);

    if (!token) {
        return <LandingPage />;
    }

    return (
        <div className="home-page">
            <header className="page-header">
                <h1>{t('my_leagues')}</h1>
                <p className="subtitle">{t('manage_leagues')}</p>
            </header>

            <section className="leagues-section">
                <h2>{t('my_leagues')}</h2>
                <div className="league-grid">
                    {leagues.length === 0 ? (
                        <div className="card" style={{ padding: '40px', textAlign: 'center', color: '#888', gridColumn: '1 / -1' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '15px' }}>⚽</div>
                            <p>{t('no_leagues_yet') || 'Non fai ancora parte di nessuna lega.'}</p>

                        </div>
                    ) : (
                        leagues.map(league => (
                            <Link key={league.id} to={`/league/${league.id}`} className="league-card">
                                <div className={`sport-badge badge-${league.sport || 'football'}`}>
                                    {sportInfo[league.sport || 'football']?.icon} {sportInfo[league.sport || 'football']?.label}
                                </div>
                                <div className="league-info">
                                    <span className="league-name">
                                        {league.name}
                                        {league.is_public && <span style={{ fontSize: '0.8em', background: '#eee', padding: '2px 6px', borderRadius: '4px', marginLeft: '8px' }}>{t('public')}</span>}
                                        {league.membership_status === 'pending' && <span style={{ fontSize: '0.8em', background: '#ffcc00', padding: '2px 6px', borderRadius: '4px', marginLeft: '8px' }}>{t('pending')}</span>}
                                    </span>
                                    <span className="league-code">
                                        {t('invite_code')}: <code>{league.invite_code}</code>
                                        {(league.active_proposals_count > 0 || league.upcoming_matches_count > 0) && (
                                            <div style={{ marginTop: '10px', fontSize: '0.9em', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                {parseInt(league.active_proposals_count) > 0 && (
                                                    (parseInt(league.user_voted_proposals_count) + parseInt(league.user_not_available_proposals_count)) >= parseInt(league.active_proposals_count) ? (
                                                        <span style={{ background: '#f5f5f5', color: '#616161', padding: '4px 8px', borderRadius: '4px' }}>📑 {t('voting')}</span>
                                                    ) : (
                                                        <span style={{ background: '#e3f2fd', color: '#0d47a1', padding: '4px 8px', borderRadius: '4px' }}>📅 {t('dates_to_vote')}</span>
                                                    )
                                                )}
                                                {parseInt(league.upcoming_matches_count) > 0 && <span style={{ background: '#e8f5e9', color: '#1b5e20', padding: '4px 8px', borderRadius: '4px' }}>⚽ {t('matches_scheduled')}</span>}
                                                {Number(league.user_playing_matches_count) > 0 && <span style={{ background: '#fff3e0', color: '#e65100', padding: '4px 8px', borderRadius: '4px', fontWeight: 'bold' }}>🚀 {t('you_play')}</span>}
                                                {Number(league.user_waitlisted_matches_count) > 0 && <span style={{ background: '#f5f5f5', color: '#616161', padding: '4px 8px', borderRadius: '4px', fontWeight: 'bold' }}>⏳ {t('waitlist_label')}</span>}
                                            </div>
                                        )}
                                    </span>
                                </div>
                                <div className="league-arrow">→</div>
                            </Link>
                        ))
                    )}
                </div>
            </section>

            {scoutingMatches.length > 0 && (
                <section className="scouting-section" style={{ marginTop: '30px' }}>
                    <div className="section-header">
                        <h2>⚽ {t('external_matches_title')}</h2>
                        <p className="subtitle">{t('external_matches_desc')}</p>
                    </div>
                    <div className="league-grid">
                        {scoutingMatches.map(match => (
                            <div key={match.request_id} className="league-card scouting-card" style={{ borderLeft: '4px solid #4caf50' }}>
                                <div className={`sport-badge badge-${match.sport || 'football'}`}>
                                    {sportInfo[match.sport || 'football']?.icon} {sportInfo[match.sport || 'football']?.label}
                                </div>
                                <div className="league-info">
                                    <span className="league-name">{match.league_name}</span>
                                    <div style={{ marginTop: '5px', fontSize: '0.9rem' }}>
                                        <strong>{new Date(match.start_time).toLocaleDateString()} {new Date(match.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong>
                                        <div style={{ color: '#666', marginTop: '2px' }}>📍 {match.location}</div>
                                        <div style={{ color: '#e65100', fontWeight: 'bold', marginTop: '4px' }}>🔥 {t('players_needed')}: {match.needed_players}</div>
                                        <div style={{ color: '#0d47a1', fontWeight: 'bold', marginTop: '4px', fontSize: '0.85rem' }}>📊 {t('match_level')}: {match.match_level}/5</div>
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => { 
                                        e.preventDefault(); 
                                        match.my_status === 'pending' ? handleWithdrawScouting(match.request_id) : handleJoinScouting(match.request_id); 
                                    }}
                                    className={match.my_status === 'pending' ? 'danger-btn' : 'primary-btn'}
                                    style={{ marginTop: '10px', width: '100%' }}
                                >
                                    {match.my_status === 'pending' ? 'Ritira Candidatura' : 'Candidati come Ospite'}
                                </button>
                                {match.my_status === 'accepted' && (
                                    <div style={{ marginTop: '8px', color: '#4caf50', fontWeight: 'bold', fontSize: '0.85rem', textAlign: 'center' }}>
                                        ✅ Candidatura Accettata!
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {leagues.length === 0 || showCreateForm ? (
                <section className="create-section card" style={{ marginTop: leagues.length > 0 ? '20px' : '0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h3 style={{ margin: 0 }}>{t('new_league')}</h3>
                        {leagues.length > 0 && <button onClick={() => setShowCreateForm(false)} className="outline-btn" style={{ background: 'transparent', color: '#666', border: 'none', padding: 0 }}>✕</button>}
                    </div>
                    <form onSubmit={handleCreateLeague} className="inline-form" style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '400px' }}>
                        <input
                            value={newLeagueName}
                            onChange={e => setNewLeagueName(e.target.value)}
                            placeholder={t('league_name_placeholder')}
                            required
                        />
                        <div className="form-group">
                            <label>{t('sport')}</label>
                            <select value={sport} onChange={e => setSport(e.target.value)} className="simple-input">
                                <option value="football">⚽ {t('football')}</option>
                                <option value="volleyball">🏐 {t('volleyball')}</option>
                                <option value="padel">🎾 {t('padel')}</option>
                                <option value="basketball">🏀 {t('basketball')}</option>
                                <option value="tennis">🎾 {t('tennis')}</option>
                                <option value="waterpolo">🤽 {t('waterpolo')}</option>
                                <option value="rugby">🏉 {t('rugby')}</option>
                            </select>
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center' }}>
                            <input
                                type="checkbox"
                                checked={isPublic}
                                onChange={e => setIsPublic(e.target.checked)}
                                style={{ marginRight: '5px' }}
                            />
                            {t('make_public')}
                        </label>
                        <button type="submit" className="primary-btn">{t('create_league')}</button>
                    </form>
                </section>
            ) : (
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    <button onClick={() => setShowCreateForm(true)} className="secondary-btn" style={{ padding: '8px 16px', fontSize: '0.9rem' }}>+ {t('new_league')}</button>
                </div>
            )}

            <ConfirmationModal
                isOpen={modalConfig.isOpen}
                onClose={() => setModalConfig({ isOpen: false, title: '', message: '' })}
                title={modalConfig.title}
                message={modalConfig.message}
                isAlert={true}
            />

            <ConfirmationModal
                isOpen={tutorialOpen}
                onClose={() => setTutorialOpen(false)}
                title={`✨ ${t('tutorial_welcome')}`}
                isAlert={true}
                noAutoClose={true}
            >
                <div style={{ textAlign: 'left', lineHeight: '1.6' }}>
                    <p style={{ fontSize: '1.05rem', color: '#333', marginBottom: '15px' }}>
                        {t('tutorial_new_league')}
                    </p>
                    <p style={{ fontSize: '1.05rem', color: '#333' }}>
                        {t('tutorial_join_league')}
                    </p>
                </div>
            </ConfirmationModal>
        </div>
    );
};

export default Home;
